import React, { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { App as AntApp, ConfigProvider } from 'antd'
import ptBR from 'antd/locale/pt_BR'

// Import contexts
import { AuthProvider } from './contexts/AuthContext'
import { OrganizationProvider } from './contexts/OrganizationContext'
import { PermissionsProvider } from './contexts/PermissionsContext'
import { ThemeProvider, useTheme } from './contexts/ThemeContext'

// Import route protection components
import ProtectedRoute, { PublicRoute, OrganizationRoute, UserRoute, RoleProtectedRoute } from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import BackendStatus from './components/BackendStatus'
import OfflineIndicator from './components/OfflineIndicator'
import { useAuth } from './contexts/AuthContext'

// Import pages
import Login from './pages/Login'
import ResetPassword from './pages/ResetPassword'
import ModulesMenu from './pages/ModulesMenu/ModulesMenu.jsx'
import AdminDashboard from './pages/admin/AdminDashboard'
import Profile from './pages/Profile'
import Onboard from './pages/Onboard/Onboard.jsx'
import WebhookStatus from './pages/admin/WebhookStatus'
import DashboardOperacional from './pages/PainelOperacional/dashOperacional'

// Import components
import Layout from './components/Layout'
import SSWComponent from './components/SSW'

function AppContent() {
  const { backendAvailable, handleBackendAvailable } = useAuth()

  // Show backend status component if backend is not available
  if (!backendAvailable) {
    return <BackendStatus onBackendAvailable={handleBackendAvailable} />
  }

  return (
    <div className="min-h-screen">
      <OfflineIndicator />
      <Routes>
        {/* Public routes - sem Navbar */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        {/* Rota removida - funcionalidade de recuperação de senha indisponível
            <Route 
              path="/forgot-password" 
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              } 
            />
            */}
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/webhook-status"
          element={<WebhookStatus />}
        />

        {/* Protected routes - com Navbar através do Layout */}
        <Route
          path="/menu"
          element={
            <ProtectedRoute>
              <Layout>
                <ModulesMenu />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboard"
          element={
            <ProtectedRoute>
              <Onboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/painel-operacional"
          element={
            <ProtectedRoute>
              <DashboardOperacional />
            </ProtectedRoute>
          }
        />
        <Route
          path="/painel-expedicao"
          element={
            <ProtectedRoute>
              <DashboardOperacional />
            </ProtectedRoute>
          }
        />
        <Route
          path="/relatorios"
          element={
            <ProtectedRoute>
              <DashboardOperacional />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ssw"
          element={
            <ProtectedRoute>
              <Layout>
                <SSWComponent />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Admin routes - protegidas por role */}
        <Route
          path="/admin"
          element={
            <RoleProtectedRoute requiredRoles={['inadmin']}>
              <AdminDashboard />
            </RoleProtectedRoute>
          }
        />

        {/* Default redirect for authenticated users */}
        <Route
          path="/dashboard"
          element={<Navigate to="/menu" replace />}
        />

        {/* Catch all route - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  )
}

function App() {
  useEffect(() => {
    const shouldRegisterSW = () => {
      const flag = String(import.meta.env.VITE_ENABLE_SW || '').toLowerCase() === 'true'
      const isFile = typeof window !== 'undefined' && window.location?.protocol === 'file:'
      const nav = typeof navigator !== 'undefined' ? navigator : null
      const conn = nav && nav.connection ? nav.connection : null
      const eff = conn && conn.effectiveType ? String(conn.effectiveType).toLowerCase() : ''
      const type = conn && conn.type ? String(conn.type).toLowerCase() : ''
      const isCellular = type === 'cellular' || ['2g', '3g', '4g', '5g'].includes(eff)
      return flag && !isFile && !isCellular
    }

    const registerServiceWorker = async () => {
      if (!shouldRegisterSW()) return
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
          registration.addEventListener('updatefound', () => { })
        } catch (error) { }
      }
    }
    const timer = setTimeout(registerServiceWorker, 500)
    return () => clearTimeout(timer)
  }, [])

  return (
    <ErrorBoundary showErrorOverlay={false}>
      <ThemeProvider>
        <ThemedApp />
      </ThemeProvider>
    </ErrorBoundary>
  )
}

function ThemedApp() {
  const { theme: currentTheme } = useTheme()

  return (
    <ConfigProvider
      theme={currentTheme}
      locale={ptBR}
    >
      <AuthProvider>
        <OrganizationProvider>
          <PermissionsProvider>
            <AntApp>
              <AppContent />
            </AntApp>
          </PermissionsProvider>
        </OrganizationProvider>
      </AuthProvider>
    </ConfigProvider>
  )
}

export default App